// GalacticCore.tsx — The luminous central bulge + disk haze of the galaxy, the
// backdrop that makes the map read as a galaxy rather than scattered dots
// (Stellaris-style). Flat additive gradient disks on the galactic plane (XZ): a
// broad cool disk haze, a warm mid halo, and a hot white core. Purely decorative
// — non-interactive, depth-write off, drawn first so stars sit on top.
import { useMemo } from 'react';
import { AdditiveBlending, DoubleSide } from 'three';
import { glowTexture } from './glow';

// Galaxy disk radius — matches generate.ts (dist = 4 + t*52 → ~56).
const DISK = 56;

interface Layer {
  scale: number;
  color: string;
  opacity: number;
  /** Lift slightly off the plane so the layers don't z-fight (depthWrite is off
   *  anyway, but a tiny offset keeps the additive stack tidy). */
  y: number;
}

// Cool wide halo → warm bulge → hot core, the classic galaxy luminosity run.
const LAYERS: Layer[] = [
  { scale: DISK * 2.0, color: '#243a6e', opacity: 0.5, y: -0.6 },
  { scale: DISK * 1.15, color: '#5a4fa0', opacity: 0.45, y: -0.4 },
  { scale: DISK * 0.55, color: '#caa35e', opacity: 0.7, y: -0.2 },
  { scale: DISK * 0.22, color: '#ffe6b0', opacity: 0.95, y: 0 },
  { scale: DISK * 0.08, color: '#fffdf5', opacity: 1, y: 0.2 },
];

export function GalacticCore() {
  const tex = useMemo(glowTexture, []);
  return (
    <group>
      {LAYERS.map((l, i) => (
        <mesh key={i} position={[0, l.y, 0]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={-20 + i}>
          <planeGeometry args={[l.scale, l.scale]} />
          <meshBasicMaterial
            map={tex}
            color={l.color}
            transparent
            opacity={l.opacity}
            blending={AdditiveBlending}
            depthWrite={false}
            depthTest={false}
            side={DoubleSide}
            toneMapped={false}
          />
        </mesh>
      ))}
    </group>
  );
}
